import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { objectKlassEquals } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import { TextareaPasteConversion } from './textarea-paste-conversion';

/**
 * Turns bullet-looking plain text into markdown lists as it is pasted.
 *
 * This replaces a normalisation that used to run on every save. Doing it here
 * means it only sees text that was actually pasted, and it sees the raw
 * clipboard string — before there is any structural indentation for it to
 * damage.
 *
 * `registerRichText` registers its own paste handler at
 * `COMMAND_PRIORITY_EDITOR`, the lowest priority, so this runs first and hands
 * back anything it does not want by returning false.
 */
export const RawTextareaPastePlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        event => {
          // `objectKlassEquals` rather than `instanceof`, so an event from
          // another window (or a jsdom realm) still matches.
          const clipboard = objectKlassEquals(event, ClipboardEvent)
            ? event.clipboardData
            : null;

          if (!clipboard) return false;

          /*
            **An image being attached, not written.** A browser hands over the page's own `<img>`
            alongside the image file, and whatever takes the file wants nothing inserted - but
            `htmlImport` turns an `<img>` into a single space, so importing it would leave that
            space sitting in the composer beside the attachment.

            Only when the file is there too: a rich paste that merely contains an image still wants
            the space, which is what keeps the words either side of it apart.
          */
          if (
            clipboard.types.includes('Files') &&
            TextareaPasteConversion.isImageOnlyHtml({
              html: clipboard.getData('text/html'),
            })
          ) {
            event.preventDefault();

            return true;
          }

          // A rich payload already imports as real list nodes.
          if (clipboard.types.includes('text/html')) {
            return false;
          }

          const text = clipboard.getData('text/plain');
          const normalized =
            TextareaPasteConversion.normalizePlainTextBullets(text);
          const selection = $getSelection();

          // Nothing to rewrite, so let the default handler do its job.
          if (normalized === text || !$isRangeSelection(selection)) {
            return false;
          }

          event.preventDefault();
          selection.insertRawText(normalized);
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
    [editor]
  );

  return null;
};
